import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { motion, AnimatePresence } from 'framer-motion';
import { useCurrency } from '../contexts/CurrencyContext';
import PackageHighlights from '../components/PackageHighlights';
import axios from 'axios';
import {
  StarIcon,
  CalendarIcon,
  UsersIcon,
  CheckIcon,
  MapPinIcon,
  PhotoIcon,
  ArrowRightIcon,
  CreditCardIcon,
  HeartIcon,
  SparklesIcon,
  GiftIcon,
  ShieldCheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import EnquiryForm from '../components/EnquiryForm';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

declare global {
  interface Window {
    FlywirePayment: any;
  }
}

// Enhanced image URL resolver with Cloudinary support
const resolveImageUrl = (img?: string): string => {
  if (!img) return '/images/placeholder-resort.jpg'; // Better fallback
  const normalized = String(img).replace(/\\/g, '/');

  // If Cloudinary URL, return as-is
  if (normalized.includes('cloudinary.com')) {
    return normalized;
  }

  // If absolute URL or data URL, return as-is
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }

  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

interface Testimonial {
  customerName: string;
  rating: number;
  comment: string;
  date: string;
}

interface Resort {
  _id: string;
  name: string;
  slug: string;
  bannerImage: string;
  image?: string; // Legacy field for backward compatibility
  gallery?: string[];
  about: string;
  destination: any;
  averageRating?: number;
  rating?: number;
  amenities: Array<string | { name: string; icon?: string; _id?: string }>;
  description: string;
  testimonials?: Testimonial[];
}

interface Package {
  _id: string;
  name: string;
  slug?: string;
  description: string;
  bannerImage: string;
  gallery: string[];
  priceUSD: number;
  priceAUD: number;
  lastMinuteDealPriceUSD?: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
  hasActiveLastMinuteDeal?: boolean;
  price?: {
    usd: number;
    aud: number;
  };
  resorts: Resort[];
  features: string[];
  inclusions: string[];
  exclusions?: string[];
  highlights?: Array<{
    title: string;
    image?: string;
    imageUrl?: string;
  }> | string[];
  premiumFeatures?: string[];
  termsAndConditions?: string[];
  validityPeriod: number;
  maxGuests: number;
  nights: number;
  packageCode: string;
  testimonials?: Array<{
    customerName: string;
    rating: number;
    review: string;
    date: string;
    location: string;
  }>;
  isActive?: boolean;
  isLive?: boolean;
  enquireEnabled?: boolean;
  bookEnabled?: boolean;
  resortSettings?: Array<{
    resortId: string;
    showExploreOption: boolean;
  }>;
}

const PackageDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currency, formatPrice } = useCurrency();
  const [enquireEnabled, setEnquireEnabled] = useState<boolean>(false);
  const [bookEnabled, setBookEnabled] = useState<boolean>(false);
  const [resortSettings, setResortSettings] = useState<Array<{
    resortId: string;
    showExploreOption: boolean;
  }>>([]);
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [selectedResortForGallery, setSelectedResortForGallery] = useState<string>('all');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: string]: boolean }>({});
  const [showAllAmenities, setShowAllAmenities] = useState<{ [key: string]: boolean }>({});
  const [selectedTab, setSelectedTab] = useState('info');
  const [selectedResort, setSelectedResort] = useState<string | null>(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState<'standard' | 'deal'>('standard');

  // Booking Modal State
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'flywire' | null>(null);
  const [showPayPalButtons, setShowPayPalButtons] = useState(false);
  const [showLoadingBeforePayPal, setShowLoadingBeforePayPal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [bookingFormData, setBookingFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+61'
  });

  const sortedResorts = useMemo(() => {
    // Safe default if data isn't loaded yet
    if (!packageData || !packageData.resorts) return [];

    return [...packageData.resorts].sort((a, b) => {
      const ratingA = a.averageRating ?? a.rating ?? 0;
      const ratingB = b.averageRating ?? b.rating ?? 0;
      return ratingB - ratingA; // Descending - highest rated first
    });
  }, [packageData]);

  // Helper functions for expandable content
  const toggleDescription = (resortId: string) => {
    setExpandedDescriptions(prev => ({ ...prev, [resortId]: !prev[resortId] }));
  };

  const toggleAmenities = (resortId: string) => {
    setShowAllAmenities(prev => ({ ...prev, [resortId]: !prev[resortId] }));
  };

  // Fast API loading with timeout
  useEffect(() => {
    const fetchPackage = async () => {
      try {
        console.log('Fetching package with ID:', id);

        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages/${id}`);

        const data = (response as any).data;
        const pkg = data.package || data;
        setPackageData(pkg);

        // Set action type settings
        setEnquireEnabled(pkg.enquireEnabled || false);
        setBookEnabled(pkg.bookEnabled !== false);
        setResortSettings(pkg.resortSettings || []);
      } catch (error) {
        console.error('Failed to fetch package:', error);
        // Set fallback state so page still renders
        setPackageData(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPackage();
    }
  }, [id]);

  // Automatically select last minute deal price if available
  useEffect(() => {
    if (packageData) {
      const hasActiveDeal = packageData.lastMinuteDealPriceAUD &&
        packageData.lastMinuteDealPriceAUD > 0 &&
        packageData.lastMinuteDealPriceAUD < packageData.priceAUD &&
        packageData.lastMinuteDealExpiryDate &&
        new Date(packageData.lastMinuteDealExpiryDate) > new Date();

      if (hasActiveDeal) {
        setSelectedPriceType('deal');
      } else {
        setSelectedPriceType('standard');
      }
    }
  }, [packageData]);

  // Fetch payment settings
  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const cached = localStorage.getItem('baligetaway-settings');
        const cacheTime = localStorage.getItem('baligetaway-settings-time');
        if (cached && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
          const settings = JSON.parse(cached);
          setPaymentSettings(settings);
          autoSelectPaymentMethod(settings);
          return;
        }
        const response = await fetch('/api/settings/public');
        if (response.ok) {
          const settings = await response.json();
          setPaymentSettings(settings);
          localStorage.setItem('baligetaway-settings', JSON.stringify(settings));
          localStorage.setItem('baligetaway-settings-time', Date.now().toString());
          autoSelectPaymentMethod(settings);
        }
      } catch (error) {
        console.error('Failed to fetch payment settings:', error);
      }
    };
    fetchPaymentSettings();
  }, []);

  const autoSelectPaymentMethod = (settings: any) => {
    const paypalEnabled = settings?.paypal?.enabled;
    const flywireEnabled = settings?.flywire?.enabled ?? true;
    if (paypalEnabled && !flywireEnabled) setPaymentMethod('paypal');
    else if (flywireEnabled && !paypalEnabled) setPaymentMethod('flywire');
  };

  // Get package price based on selected type
  const getPackagePrice = () => {
    if (!packageData) return 0;
    if (selectedPriceType === 'deal' && packageData.lastMinuteDealPriceAUD) {
      return currency === 'AUD' ? packageData.lastMinuteDealPriceAUD : (packageData.lastMinuteDealPriceUSD || packageData.priceUSD);
    }
    return currency === 'AUD' ? packageData.priceAUD : packageData.priceUSD;
  };

  // Validate booking form
  const validateBookingForm = () => {
    if (!bookingFormData.firstName || !bookingFormData.lastName || !bookingFormData.email || !bookingFormData.phone) {
      setPaymentError('Please fill in all required fields');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingFormData.email)) {
      setPaymentError('Please enter a valid email address');
      return false;
    }
    if (bookingFormData.phone.length < 8) {
      setPaymentError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  // PayPal handlers
  const createPayPalOrder = async () => {
    const cleanPhone = bookingFormData.phone.replace(/[\s\-\(\)]/g, '');
    const response = await axios.post('/api/paypal/create-order', {
      amount: getPackagePrice(),
      currency,
      packageId: packageData?._id,
      packageName: packageData?.name,
      firstName: bookingFormData.firstName,
      lastName: bookingFormData.lastName,
      email: bookingFormData.email,
      phone: `${bookingFormData.countryCode}${cleanPhone}`
    });
    if (response.data.success) return response.data.orderId;
    throw new Error(response.data.message);
  };

  const onPayPalApprove = async (data: any) => {
    try {
      setPaymentProcessing(true);
      const cleanPhone = bookingFormData.phone.replace(/[\s\-\(\)]/g, '');
      const response = await axios.post('/api/paypal/capture-order', {
        orderId: data.orderID,
        customerData: {
          firstName: bookingFormData.firstName,
          lastName: bookingFormData.lastName,
          email: bookingFormData.email,
          phone: `${bookingFormData.countryCode}${cleanPhone}`,
          packageId: packageData?._id,
          packageName: packageData?.name
        }
      });
      if (response.data.success) {
        navigate(`/payment-success?status=success&reference=${response.data.bookingCode}&transactionId=${response.data.transactionId}&amount=${response.data.amount}&currency=${response.data.currency}&email=${bookingFormData.email}&name=${encodeURIComponent(bookingFormData.firstName + ' ' + bookingFormData.lastName)}&package=${encodeURIComponent(packageData?.name || '')}&paymentMethod=PayPal`);
      } else {
        setPaymentError(response.data.message || 'Payment capture failed');
        setPaymentProcessing(false);
      }
    } catch (err: any) {
      setPaymentError(err.response?.data?.message || 'Failed to capture payment');
      setPaymentProcessing(false);
    }
  };

  // Flywire handler
  const handleFlywirePayment = async () => {
    if (!validateBookingForm()) return;
    setPaymentProcessing(true);
    setPaymentError('');

    try {
      const amount = getPackagePrice();
      const cleanPhone = bookingFormData.phone.replace(/[\s\-\(\)]/g, '');
      if (typeof window.FlywirePayment === 'undefined') {
        setPaymentError('Flywire payment system not loaded. Please refresh.');
        setPaymentProcessing(false);
        return;
      }
      const isLive = paymentSettings?.flywire?.environment === 'live';
      const recipientCode = isLive ? (currency === 'AUD' ? 'FQD' : 'FQG') : 'IRG';
      const transactionId = `TXN${Date.now()}`;
      const returnUrl = `${window.location.origin}/payment-success?status=pending&reference=${transactionId}&transactionId=${transactionId}&email=${bookingFormData.email}&name=${encodeURIComponent(bookingFormData.firstName + ' ' + bookingFormData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData?.name || '')}&packageId=${packageData?._id || ''}&phone=${encodeURIComponent(bookingFormData.countryCode + bookingFormData.phone.replace(/[\s\-\(\)]/g, ''))}&paymentMethod=Flywire`;

      const flywireConfig = {
        env: isLive ? 'prod' : 'demo',
        recipientCode,
        amount: parseFloat(amount.toFixed(2)),
        currency: currency.toUpperCase(),
        firstName: bookingFormData.firstName.trim(),
        lastName: bookingFormData.lastName.trim(),
        email: bookingFormData.email.trim().toLowerCase(),
        phone: `${bookingFormData.countryCode}${cleanPhone}`,
        recipientFields: {
          booking_reference: transactionId,
          web_source: window.location.hostname,
          package_name: packageData?.name || '',
          package_code: packageData?.packageCode || ''
        },
        requestPayerInfo: true,
        returnUrl,
        callbackUrl: `${window.location.origin}/api/payment/webhook/${currency.toLowerCase()}`,
        callbackId: "BaliGetaway",
        callbackVersion: "2",
        onSuccess: async (result: any) => {
          const flywirePaymentId = result?.paymentId || result?.payment_id || transactionId;
          try {
            await axios.post('/api/bookings/create', {
              firstName: bookingFormData.firstName,
              lastName: bookingFormData.lastName,
              email: bookingFormData.email,
              phone: `${bookingFormData.countryCode}${cleanPhone}`,
              amount, currency,
              packageId: packageData?._id,
              transactionId: flywirePaymentId,
              paymentMethod: 'flywire'
            });
          } catch (e) { console.error('Booking creation failed:', e); }
          navigate(`/payment-success?status=success&transactionId=${flywirePaymentId}&email=${bookingFormData.email}&name=${encodeURIComponent(bookingFormData.firstName + ' ' + bookingFormData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData?.name || '')}&paymentMethod=Flywire`);
        },
        onCancel: () => {
          setPaymentError('Payment was cancelled.');
          setPaymentProcessing(false);
        },
        onInvalidInput: (errors: any[]) => {
          setPaymentError(errors.map(e => e.msg || e.message).join('\n'));
          setPaymentProcessing(false);
        }
      };

      const modal = window.FlywirePayment.initiate(flywireConfig);
      if (modal) {
        modal.render();
        setPaymentProcessing(false);
        setShowBookingModal(false);
      } else {
        throw new Error('Failed to initiate payment modal');
      }
    } catch (err: any) {
      setPaymentError(err.message || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  // Handle proceed to payment
  const handleProceedToPayment = () => {
    if (!validateBookingForm()) return;
    setPaymentError('');
    if (paymentMethod === 'paypal') {
      setShowLoadingBeforePayPal(true);
      setTimeout(() => {
        setShowLoadingBeforePayPal(false);
        setShowPayPalButtons(true);
      }, 1500);
    } else if (paymentMethod === 'flywire') {
      handleFlywirePayment();
    } else {
      setPaymentError('Please select a payment method');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading package details...</p>
        </div>
      </div>
    );
  }

  // Fallback removed: use live data only
  const fallbackPackageData: Package | null = null;

  // Use real data only
  const displayData = packageData;

  // Early return if no data - No hooks after this point!
  if (!displayData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Package not found</h2>
          <p className="text-gray-600 mb-4">The package you're looking for doesn't exist.</p>
          <Link to="/packages" className="btn-primary">
            Browse All Packages
          </Link>
        </div>
      </div>
    );
  }

  // Price helper (computed but currently unused in UI)
  const currentPrice = currency === 'USD'
    ? (displayData?.priceUSD || 0)
    : (displayData?.priceAUD || 0);

  // Gallery images with fallback to bannerImage
  const galleryImages = (Array.isArray(displayData.gallery) && displayData.gallery.length
    ? displayData.gallery
    : (displayData.bannerImage ? [displayData.bannerImage] : []));

  return (
    <div>
      <Helmet>
        <title>{displayData.name} - Bali Getaway Packages</title>
        <meta name="description" content={displayData.description} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50">
        {/* Hero Section with Modern Image Gallery */}
        <div className="relative bg-white shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 to-orange-400/10"></div>
          <div className="container mx-auto px-4 py-8 md:py-12 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
              {/* Enhanced Image Gallery */}
              <div className="space-y-6">
                <div className="relative h-56 md:h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={resolveImageUrl(galleryImages[selectedImage])}
                    alt={displayData.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  <button
                    onClick={() => setIsFavorite(!isFavorite)}
                    className="absolute top-4 right-4 md:top-6 md:right-6 p-2 md:p-3 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-110"
                  >
                    {isFavorite ? (
                      <HeartIconSolid className="h-6 w-6 text-red-500" />
                    ) : (
                      <HeartIcon className="h-6 w-6 text-white" />
                    )}
                  </button>

                  {/* Image counter - only show if multiple images */}
                  {galleryImages.length > 1 && (
                    <div className="absolute bottom-3 left-3 md:bottom-6 md:left-6 bg-black/50 backdrop-blur-sm text-white px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-medium">
                      {selectedImage + 1} / {galleryImages.length}
                    </div>
                  )}
                </div>

                {/* Gallery thumbnails - only show if multiple images */}
                {galleryImages.length > 1 && (
                  <>
                    <div className="md:hidden -mx-1 overflow-x-auto">
                      <div className="flex gap-3 px-1">
                        {galleryImages.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImage(index)}
                            className={`w-28 h-20 rounded-xl overflow-hidden border-3 transition-all duration-300 ${selectedImage === index
                              ? 'border-orange-500 shadow-lg'
                              : 'border-transparent'
                              }`}
                          >
                            <img
                              src={resolveImageUrl(image)}
                              alt={`Gallery ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="hidden md:grid grid-cols-4 gap-3">
                      {galleryImages.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`h-24 rounded-2xl overflow-hidden border-3 transition-all duration-300 hover:scale-105 ${selectedImage === index
                            ? 'border-orange-500 shadow-lg transform scale-105'
                            : 'border-transparent hover:border-orange-300'
                            }`}
                        >
                          <img
                            src={resolveImageUrl(image)}
                            alt={`Gallery ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Enhanced Package Details */}
              <div className="space-y-4 md:space-y-6">
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    {/* <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold tracking-wide">
                      {displayData.packageCode}
                    </span>          */}
                  </div>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-6 leading-tight">
                    {displayData.name}
                  </h1>
                  <p className={`text-gray-600 text-sm md:text-xl leading-relaxed break-words max-w-none overflow-hidden ${showFullDescription ? '' : 'line-clamp-3 md:line-clamp-none'}`}>
                    {displayData.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowFullDescription(v => !v)}
                    className="mt-1 text-orange-600 hover:text-orange-700 font-medium md:hidden"
                  >
                    {showFullDescription ? 'Show less' : 'Read more'}
                  </button>
                </div>

                {/* Feature badges - compact on mobile */}
                <div className="md:hidden">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-800">
                      <CalendarIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="text-xs font-semibold truncate">{displayData.validityPeriod} months</span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-800">
                      <UsersIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-xs font-semibold truncate">Up to {displayData.maxGuests}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-800">
                      <CalendarIcon className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                      <span className="text-xs font-semibold truncate">{displayData.nights || 3} nights</span>
                    </div>
                    {displayData.resorts.length > 1 && (
                      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200 text-purple-800">
                        <MapPinIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                        <span className="text-xs font-semibold truncate">{displayData.resorts.length > 2 ? `Choose from ${displayData.resorts.length} resorts` : `${displayData.resorts.length} Resorts Available`}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Feature Cards grid - desktop */}
                <div className={`hidden md:grid ${displayData.resorts.length > 1 ? 'grid-cols-4' : 'grid-cols-3'} gap-4`}>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl text-center border border-blue-200 hover:shadow-lg transition-shadow">
                    <CalendarIcon className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                    <p className="text-sm text-blue-700 font-medium mb-1">Validity</p>
                    <p className="font-bold text-blue-900 text-lg">{displayData.validityPeriod} months</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl text-center border border-green-200 hover:shadow-lg transition-shadow">
                    <UsersIcon className="h-8 w-8 text-green-600 mx-auto mb-3" />
                    <p className="text-sm text-green-700 font-medium mb-1">Max Guests</p>
                    <p className="font-bold text-green-900 text-lg">Up to {displayData.maxGuests}</p>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl text-center border border-indigo-200 hover:shadow-lg transition-shadow">
                    <CalendarIcon className="h-8 w-8 text-indigo-600 mx-auto mb-3" />
                    <p className="text-sm text-indigo-700 font-medium mb-1">Duration</p>
                    <p className="font-bold text-indigo-900 text-lg">{displayData.nights || 3} nights</p>
                  </div>
                  {displayData.resorts.length > 1 && (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl text-center border border-purple-200 hover:shadow-lg transition-shadow">
                      <MapPinIcon className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                      <p className="text-sm text-purple-700 font-medium mb-1">Accommodation</p>
                      <p className="font-bold text-purple-900 text-lg">{displayData.resorts.length > 2 ? `Choose from ${displayData.resorts.length} resorts` : `${displayData.resorts.length} Resorts Available`}</p>
                    </div>
                  )}
                </div>

                {/* Pricing and CTA */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 md:p-6 rounded-2xl border border-orange-200 shadow-lg">
                  {/* Last Minute Deal Banner */}
                  {/* {displayData.hasActiveLastMinuteDeal && displayData.lastMinuteDealPriceUSD && displayData.lastMinuteDealPriceAUD && (
                    <div className="mb-4 bg-gradient-to-r from-red-500 to-pink-600 text-white p-3 md:p-4 rounded-xl shadow-lg animate-pulse">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">⚡</span>
                          <div>
                            <p className="font-bold text-sm md:text-base">LAST MINUTE DEAL!</p>
                            <p className="text-xs opacity-90">Book within 3 months</p>
                          </div>
                        </div>
                        {displayData.lastMinuteDealExpiryDate && (
                          <div className="text-right">
                            <p className="text-xs font-semibold">Expires:</p>
                            <p className="text-xs md:text-sm font-bold">
                              {new Date(displayData.lastMinuteDealExpiryDate).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )} */}

                  {/* Professional Pricing Section */}
                  <div className="mb-4">
                    {displayData.hasActiveLastMinuteDeal && displayData.lastMinuteDealPriceAUD ? (
                      <div className="space-y-3">
                        {/* Price Cards */}
                        <div className="grid grid-cols-2 gap-3">
                          {/* Last Minute Deal Price Card */}
                          <button
                            onClick={() => setSelectedPriceType('deal')}
                            className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${selectedPriceType === 'deal'
                              ? 'border-red-500 bg-red-50 shadow-lg ring-2 ring-red-200'
                              : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-md'
                              }`}
                          >
                            {/* Deal Badge */}
                            <div className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                              <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                              SAVE A${(displayData.priceAUD - displayData.lastMinuteDealPriceAUD).toFixed(0)}
                            </div>

                            <div className="mb-1">
                              <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">⚡ Last Minute Deal</p>
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl md:text-3xl font-bold text-red-600">
                                A${displayData.lastMinuteDealPriceAUD.toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 line-through">
                              Was A${displayData.priceAUD.toLocaleString()}
                            </p>
                            {selectedPriceType === 'deal' && (
                              <div className="mt-2 flex items-center gap-1 text-xs text-red-600 font-medium">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Selected
                              </div>
                            )}
                          </button>

                          {/* Standard Price Card */}
                          <button
                            onClick={() => setSelectedPriceType('standard')}
                            className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${selectedPriceType === 'standard'
                              ? 'border-orange-500 bg-orange-50 shadow-lg ring-2 ring-orange-200'
                              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
                              }`}
                          >
                            <div className="mb-1">
                              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Standard Price</p>
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl md:text-3xl font-bold text-orange-600">
                                A${displayData.priceAUD.toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Flexible booking
                            </p>
                            {selectedPriceType === 'standard' && (
                              <div className="mt-2 flex items-center gap-1 text-xs text-orange-600 font-medium">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Selected
                              </div>
                            )}
                          </button>
                        </div>

                        {/* Last Minute Deal Notice */}
                        {selectedPriceType === 'deal' && (
                          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3 animate-fadeIn">
                            <div className="flex gap-2">
                              <div className="flex-shrink-0">
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-red-800 mb-1">⏰ Last Minute Deal Requirement</h4>
                                <p className="text-xs text-red-700 leading-relaxed">
                                  This offer requires booking within <span className="font-bold">3 months from your enquiry date</span>. Book now to secure this amazing discount!
                                </p>
                                {displayData.lastMinuteDealExpiryDate && (
                                  <p className="text-xs text-red-600 font-medium mt-1">
                                    Offer expires: {new Date(displayData.lastMinuteDealExpiryDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 rounded-xl border-2 border-orange-500 bg-orange-50">
                        <p className="text-xs text-orange-700 font-medium mb-1 uppercase tracking-wide">Starting from</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl md:text-4xl font-bold text-orange-600">
                            A${displayData.priceAUD.toLocaleString()}
                          </span>
                          <span className="text-orange-600 font-medium text-sm">per voucher</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Subtle promo pill inside pricing card */}
                  {/* <div className="mb-3 md:mb-4">
                    <div className="flex items-center gap-2 rounded-lg bg-white/90 border border-orange-200 px-3 py-2 shadow-sm">
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-orange-100 flex items-center justify-center border border-orange-200 flex-shrink-0">
                        <GiftIcon className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs md:text-sm font-semibold leading-tight text-orange-800">
                          Free holiday in Cambodia or Vietnam
                          <span className="block md:inline md:ml-1 text-orange-600 font-bold">Worth A$999</span>
                        </p>
                      </div>
                    </div>
                  </div> */}

                  {/* CTA Button - prominent placement */}
                  <div className="flex justify-center mb-3">
                    {enquireEnabled ? (
                      <button
                        onClick={() => setShowEnquiryForm(true)}
                        className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-xl hover:from-orange-600 hover:via-amber-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center text-base md:text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                      >
                        📋 Enquire Now
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                      </button>
                    ) : bookEnabled ? (
                      <button
                        onClick={() => setShowBookingModal(true)}
                        className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-xl hover:from-orange-600 hover:via-amber-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center text-base md:text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                      >
                        🎯 Book Now
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                      </button>
                    ) : null}
                  </div>

                  <div className="hidden md:flex items-center justify-center space-x-6 mt-3 text-sm text-orange-700">
                    {/* <div className="flex items-center space-x-1">
                      <CheckIcon className="h-4 w-4" />
                      <span>Secure Payment</span>
                    </div> */}
                    <div className="flex items-center space-x-1">
                      <CheckIcon className="h-4 w-4" />
                      <span>Instant Voucher</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CheckIcon className="h-4 w-4" />
                      <span>Flexible Booking</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>




        {/* Resort Selection - Compact Modern Design - Show if at least 1 resort */}
        {displayData.resorts && displayData.resorts.length >= 1 && (
          <div className="py-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                  {displayData.resorts.length === 1 ? 'Your Resort' : 'Choose Your Perfect Resort'}
                </h2>
                {displayData.resorts.length > 2 && (
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Choose from <span className="font-semibold text-blue-600">{displayData.resorts.length} resorts</span> available
                  </p>
                )}
              </div>

              <div className={`${displayData.resorts.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'}`}>
                {sortedResorts.map((resort, index) => (
                  <div
                    key={resort._id}
                    className={`group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-blue-200 transform hover:-translate-y-2 ${displayData.resorts.length === 1 ? 'w-full max-w-lg' : ''}`}
                  >
                    {/* Selection Badge - only show if multiple resorts */}
                    {displayData.resorts.length > 1 && (
                      <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        Option {index + 1}
                      </div>
                    )}
                    {/* Resort Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={resolveImageUrl(resort.bannerImage || resort.image)}
                        alt={resort.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          // Use a placeholder image URL that will work
                          target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop';
                        }}
                      />

                      {/* Overlay Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Rating Badge */}
                      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-lg">
                        <div className="flex items-center space-x-1">
                          <StarIcon className="h-4 w-4 text-yellow-500 fill-current" />
                          <span className="text-sm font-bold text-gray-900">
                            {resort.averageRating ?? resort.rating ?? 4}
                          </span>
                        </div>
                      </div>

                      {/* Selection Indicator - only show if multiple resorts */}
                      {displayData.resorts.length > 1 && (
                        <div className="absolute top-4 right-20 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                          <div className="w-6 h-6 border-2 border-blue-500 rounded-full flex items-center justify-center">
                            <div className="w-3 h-3 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                        </div>
                      )}

                      {/* Hover CTA */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div className="flex flex-col items-center gap-2">
                          <div className="bg-white/95 backdrop-blur-sm text-blue-600 font-bold py-2 px-4 rounded-xl shadow-lg">
                            {displayData.resorts.length === 1 ? '✓ Included in Package' : '✓ Available for Selection'}
                          </div>
                          <Link
                            to={`/resorts/${resort._id}`}
                            className="bg-blue-600 text-white font-medium py-2 px-4 rounded-xl hover:bg-blue-700 transition-colors duration-300"
                          >
                            View Details →
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Resort Info */}
                    <div className="p-6 space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                            {resort.name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-2">
                            <MapPinIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {(resort as any).destination?.name || (resort as any).destination || 'Bali'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="text-gray-600 text-sm leading-relaxed">
                        {(() => {
                          // Debug logging to see resort data structure
                          console.log('🏨 Resort data for', resort.name, ':', {
                            about: resort.about,
                            description: resort.description,
                            _id: resort._id,
                            amenities: resort.amenities?.length || 0
                          });

                          const description = resort.about || resort.description || 'Experience luxury and comfort at this premium resort with world-class amenities and exceptional service.';
                          const isExpanded = expandedDescriptions[resort._id];
                          const shouldTruncate = description.length > 120;

                          if (!shouldTruncate) {
                            return <p>{description}</p>;
                          }

                          return (
                            <div>
                              <p className={isExpanded ? '' : 'line-clamp-2'}>
                                {isExpanded ? description : `${description.slice(0, 120)}...`}
                              </p>
                              <button
                                onClick={() => toggleDescription(resort._id)}
                                className="text-orange-600 hover:text-orange-700 text-xs font-medium mt-1 focus:outline-none"
                              >
                                {isExpanded ? 'Show less' : 'Know more'}
                              </button>
                            </div>
                          );
                        })()
                        }
                      </div>

                      {/* Amenities */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center">
                          <SparklesIcon className="h-4 w-4 mr-2 text-yellow-500" />
                          Top Amenities
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const showAll = showAllAmenities[resort._id];
                            const amenitiesToShow = showAll ? resort.amenities : resort.amenities.slice(0, 4);

                            return (
                              <>
                                {amenitiesToShow.map((amenity, amenityIndex) => {
                                  const amenityName = typeof amenity === 'string' ? amenity : amenity?.name || 'Amenity';
                                  return (
                                    <span
                                      key={amenityIndex}
                                      className="inline-flex items-center bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium border border-blue-200 hover:from-blue-100 hover:to-purple-100 transition-colors"
                                    >
                                      ✨ {amenityName}
                                    </span>
                                  );
                                })}
                                {resort.amenities.length > 4 && (
                                  <button
                                    onClick={() => toggleAmenities(resort._id)}
                                    className="inline-flex items-center bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1 rounded-full text-xs font-medium border border-orange-200 transition-colors focus:outline-none"
                                  >
                                    {showAll
                                      ? 'Show less'
                                      : `+${resort.amenities.length - 4} more`
                                    }
                                  </button>
                                )}
                              </>
                            );
                          })()
                          }
                        </div>
                      </div>

                      {/* CTA Button */}
                      {(() => {
                        const resortSetting = resortSettings.find(s => s.resortId === resort._id);
                        const showExplore = resortSetting ? resortSetting.showExploreOption : true; // Default to true if not specified

                        return showExplore ? (
                          <div className="pt-4 border-t border-gray-100">
                            <Link
                              to={`/resorts/${resort._id}`}
                              className="w-full inline-flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold py-3 px-4 rounded-2xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                              View Resort Option
                              <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </Link>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Package Highlights - New Cloudinary Component */}
        <PackageHighlights
          highlights={displayData.highlights || []}
          packageName={displayData.name}
        />

        {/* Complete Package Information - Premium 4-Column Design */}
        <div className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-300 to-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-yellow-300 to-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />

          <div className="container mx-auto px-4 relative">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full mb-6">
                <GiftIcon className="h-6 w-6 text-orange-600 mr-2" />
                <span className="text-orange-800 font-semibold">Complete Package Details</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Everything You Need to Know
                </span>
              </h2>
              {/* <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Discover comprehensive details about your package including what's included, premium features, exclusions, and important terms
              </p> */}
            </div>

            {/* 3-Column Full Width Premium Design */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

              {/* What's Included - Enhanced Full Width Design */}
              {displayData.inclusions && displayData.inclusions.length > 0 && (
                <div className="group relative transform hover:scale-105 transition-all duration-500">
                  <div className="absolute -inset-2 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 rounded-3xl blur-lg opacity-30 group-hover:opacity-60 transition-all duration-500" />
                  <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-green-200/50 hover:shadow-3xl transition-all duration-500 h-full">
                    {/* Header with Animation */}
                    <div className="flex items-center mb-8">
                      <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-xl transform rotate-3 group-hover:rotate-6 transition-transform duration-500">
                          <CheckIcon className="h-8 w-8 text-white" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full animate-ping" />
                      </div>
                      <div className="ml-6">
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                          What's Included
                        </h3>
                        {/* <p className="text-green-700 font-semibold">✨ {displayData.inclusions.length} Amazing Features</p> */}
                      </div>
                    </div>

                    {/* Content - Enhanced with Animation */}
                    <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                      {displayData.inclusions.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-4 p-4 rounded-2xl bg-green-50/50 hover:bg-green-100/70 transition-all duration-300 transform hover:translate-x-2 group/item shadow-sm hover:shadow-md"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl flex items-center justify-center shadow-lg group-hover/item:shadow-xl transition-shadow">
                            <CheckIcon className="h-5 w-5 text-white" />
                          </div>
                          <p className="text-gray-800 font-semibold leading-relaxed group-hover/item:text-green-700 transition-colors">
                            {item}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Premium Features - Enhanced Full Width Design */}
              <div className="group relative transform hover:scale-105 transition-all duration-500">
                <div className="absolute -inset-2 bg-gradient-to-r from-purple-400 via-pink-500 to-rose-500 rounded-3xl blur-lg opacity-30 group-hover:opacity-60 transition-all duration-500" />
                <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-purple-200/50 hover:shadow-3xl transition-all duration-500 h-full">
                  {/* Header with Animation */}
                  <div className="flex items-center mb-8">
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-500">
                        <SparklesIcon className="h-8 w-8 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-400 rounded-full animate-ping" />
                    </div>
                    <div className="ml-6">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        Premium Features
                      </h3>
                      {/* <p className="text-purple-700 font-semibold">
                        🌟 {(displayData.premiumFeatures || displayData.features || []).length} Luxury Experiences
                      </p> */}
                    </div>
                  </div>

                  {/* Content - Enhanced with Animation */}
                  <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                    {(() => {
                      const features = displayData.premiumFeatures || displayData.features || [];
                      return features.length > 0
                        ? features.map((feature: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-start space-x-4 p-4 rounded-2xl bg-purple-50/50 hover:bg-purple-100/70 transition-all duration-300 transform hover:translate-x-2 group/item shadow-sm hover:shadow-md"
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            <div className="flex-shrink-0">
                              {/* Handle new image + title format or fallback to old string format */}
                              {typeof feature === 'object' && feature.image ? (
                                <img
                                  src={feature.image}
                                  alt={feature.title || 'Premium Feature'}
                                  className="w-12 h-12 object-cover rounded-xl border-2 border-purple-300 shadow-lg group-hover/item:shadow-xl transition-shadow"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl flex items-center justify-center shadow-lg group-hover/item:shadow-xl transition-shadow">
                                  <StarIcon className="h-5 w-5 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-800 font-semibold leading-relaxed group-hover/item:text-purple-700 transition-colors">
                                {typeof feature === 'object' ? feature.title : feature}
                              </p>
                              {typeof feature === 'object' && (
                                <p className="text-xs text-purple-600 mt-1">Premium Feature</p>
                              )}
                            </div>
                          </div>
                        ))
                        : (
                          <div className="space-y-4">
                            {[
                              { title: 'Premium resort accommodations with luxury amenities', image: null },
                              { title: 'Exclusive member benefits and priority services', image: null },
                              { title: '24/7 concierge support for personalized assistance', image: null },
                              { title: 'Complimentary room upgrades (subject to availability)', image: null },
                              { title: 'Access to exclusive resort facilities and pools', image: null }
                            ].map((feature, index) => (
                              <div
                                key={index}
                                className="flex items-start space-x-4 p-4 rounded-2xl bg-purple-50/50 hover:bg-purple-100/70 transition-all duration-300 transform hover:translate-x-2 group/item shadow-sm hover:shadow-md"
                                style={{ animationDelay: `${index * 0.1}s` }}
                              >
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl flex items-center justify-center shadow-lg group-hover/item:shadow-xl transition-shadow">
                                  <StarIcon className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-gray-800 font-semibold leading-relaxed group-hover/item:text-purple-700 transition-colors">
                                    {feature.title}
                                  </p>
                                  <p className="text-xs text-purple-600 mt-1">Premium Feature</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                    })()
                    }
                  </div>
                </div>
              </div>

              {/* What's Not Included / Exclusions - Enhanced Full Width Design */}
              {displayData.exclusions && displayData.exclusions.length > 0 && (
                <div className="group relative transform hover:scale-105 transition-all duration-500">
                  <div className="absolute -inset-2 bg-gradient-to-r from-red-400 via-rose-500 to-pink-500 rounded-3xl blur-lg opacity-30 group-hover:opacity-60 transition-all duration-500" />
                  <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-red-200/50 hover:shadow-3xl transition-all duration-500 h-full">
                    {/* Header with Animation */}
                    <div className="flex items-center mb-8">
                      <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-xl transform rotate-3 group-hover:rotate-6 transition-transform duration-500">
                          <XMarkIcon className="h-8 w-8 text-white" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-400 rounded-full animate-ping" />
                      </div>
                      <div className="ml-6">
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                          Not Included
                        </h3>
                        {/* <p className="text-red-700 font-semibold">⚠️ {displayData.exclusions.length} Important Exclusions</p> */}
                      </div>
                    </div>

                    {/* Content - Enhanced with Animation */}
                    <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                      {displayData.exclusions.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-4 p-4 rounded-2xl bg-red-50/50 hover:bg-red-100/70 transition-all duration-300 transform hover:translate-x-2 group/item shadow-sm hover:shadow-md"
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-red-400 to-rose-400 rounded-xl flex items-center justify-center shadow-lg group-hover/item:shadow-xl transition-shadow">
                            <XMarkIcon className="h-5 w-5 text-white" />
                          </div>
                          <p className="text-gray-800 font-semibold leading-relaxed group-hover/item:text-red-700 transition-colors">
                            {item}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Important Notice */}
            {/* <div className="mt-12 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-yellow-800 mb-2">Important Information</h4>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    Please review all package details, inclusions, exclusions, and terms & conditions carefully before making your purchase. 
                    All information is subject to change without notice. For questions or clarifications, please contact our support team.
                  </p>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Custom Scrollbar Styles */}
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: linear-gradient(to bottom, #6366f1, #8b5cf6);
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(to bottom, #4f46e5, #7c3aed);
          }
          
          /* Testimonials Carousel Styles */
          .testimonials-swiper .swiper-pagination-bullet {
            background: #d1d5db;
            opacity: 1;
          }
          .testimonials-swiper .swiper-pagination-bullet-active {
            background: linear-gradient(135deg, #f59e0b, #ea580c);
          }
          .testimonials-swiper .swiper-slide {
            height: auto;
          }
          
          /* Gallery Carousel Styles */
          .gallery-swiper .swiper-pagination-bullet {
            background: #d1d5db;
            opacity: 1;
          }
          .gallery-swiper .swiper-pagination-bullet-active {
            background: linear-gradient(135deg, #f59e0b, #ea580c);
          }
          .gallery-swiper .swiper-slide {
            height: auto;
          }
        `}</style>

        {/* Resort Gallery Filter Section */}
        {displayData.resorts && displayData.resorts.length > 0 && (
          <div className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Resort Gallery</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Explore stunning images from our featured resorts
                </p>
              </div>

              {/* Resort Filter Dropdown */}
              {displayData.resorts.length > 1 && (
                <div className="max-w-md mx-auto mb-8">
                  <select
                    value={selectedResortForGallery}
                    onChange={(e) => setSelectedResortForGallery(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white shadow-lg"
                  >
                    <option value="all">All Resorts</option>
                    {sortedResorts.map((resort) => (
                      <option key={resort._id} value={resort._id}>
                        {resort.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Gallery Carousel */}
              <div className="relative">
                {(() => {
                  const filteredResorts = selectedResortForGallery === 'all'
                    ? sortedResorts
                    : sortedResorts.filter(resort => resort._id === selectedResortForGallery);

                  const allImages: Array<{ image: string; resortName: string }> = [];

                  filteredResorts.forEach(resort => {
                    // Add banner image
                    if (resort.bannerImage) {
                      allImages.push({ image: resort.bannerImage, resortName: resort.name });
                    }
                    // Add gallery images
                    if (resort.gallery && Array.isArray(resort.gallery)) {
                      resort.gallery.forEach(img => {
                        allImages.push({ image: img, resortName: resort.name });
                      });
                    }
                  });

                  return allImages.length > 0 ? (
                    <Swiper
                      modules={[Navigation, Pagination, Autoplay]}
                      slidesPerView={1}
                      spaceBetween={20}
                      pagination={{
                        clickable: true,
                        dynamicBullets: true,
                      }}
                      navigation={{
                        nextEl: '.swiper-button-next-gallery',
                        prevEl: '.swiper-button-prev-gallery',
                      }}
                      autoplay={{
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }}
                      breakpoints={{
                        640: {
                          slidesPerView: 2,
                          spaceBetween: 20,
                        },
                        1024: {
                          slidesPerView: 3,
                          spaceBetween: 30,
                        },
                        1280: {
                          slidesPerView: 4,
                          spaceBetween: 30,
                        },
                      }}
                      className="gallery-swiper pb-12"
                    >
                      {allImages.map((item, index) => (
                        <SwiperSlide key={index}>
                          <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer aspect-[4/3]">
                            <img
                              src={resolveImageUrl(item.image)}
                              alt={item.resortName}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            />
                            {/* Resort name badge - always visible */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                              <div className="flex items-center gap-2">
                                <MapPinIcon className="h-4 w-4 text-orange-400 flex-shrink-0" />
                                <p className="text-white font-semibold text-sm truncate">{item.resortName}</p>
                              </div>
                            </div>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  ) : (
                    <div className="text-center py-12">
                      <PhotoIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No gallery images available for the selected resort</p>
                    </div>
                  );
                })()}

                {/* Custom Navigation Buttons */}
                <button className="swiper-button-prev-gallery absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:text-orange-600 hover:border-orange-300 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button className="swiper-button-next-gallery absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:text-orange-600 hover:border-orange-300 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resort Reviews */}
        <div className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">What Guests Say About These Resorts</h2>

            {/* Testimonials Carousel */}
            <div className="relative">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                slidesPerView={1}
                spaceBetween={30}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                navigation={{
                  nextEl: '.swiper-button-next-testimonials',
                  prevEl: '.swiper-button-prev-testimonials',
                }}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                }}
                className="testimonials-swiper"
              >
                {sortedResorts.flatMap((resort, resortIndex) =>
                  (resort.testimonials || []).map((testimonial, index) => (
                    <SwiperSlide key={`${resortIndex}-${index}`}>
                      <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow h-full">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-1">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                            ))}
                            {[...Array(5 - testimonial.rating)].map((_, i) => (
                              <StarIcon key={i} className="h-5 w-5 text-gray-300" />
                            ))}
                          </div>
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                            {resort.name}
                          </span>
                        </div>

                        <p className="text-gray-700 mb-4 line-clamp-3">"{testimonial.comment}"</p>

                        <div className="border-t pt-4">
                          <p className="font-semibold text-gray-900">{testimonial.customerName}</p>
                          <p className="text-sm text-gray-500">{new Date(testimonial.date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))
                )}
              </Swiper>

              {/* Custom Navigation Buttons */}
              <button className="swiper-button-prev-testimonials absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:text-orange-600 hover:border-orange-300 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button className="swiper-button-next-testimonials absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:text-orange-600 hover:border-orange-300 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Fallback when no reviews */}
            {sortedResorts.every(resort => !resort.testimonials || resort.testimonials.length === 0) && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <StarIcon className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg">Resort reviews will appear here once guests share their experiences.</p>
              </div>
            )}
          </div>
        </div>



        {/* CTA Section - Enhanced With Dynamic Button */}
        <div className="bg-gradient-to-br from-yellow-600 to-orange-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready for Your Ultimate Bali Experience?
            </h2>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              Book your dream vacation to paradise today and create memories that will last a lifetime.
            </p>

            {/* Dynamic Button Logic */}
            {displayData._id ? (
              <div className="flex justify-center">
                {enquireEnabled ? (
                  <button
                    onClick={() => setShowEnquiryForm(true)}
                    className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 text-white font-bold py-4 md:py-5 px-8 md:px-10 rounded-xl hover:from-orange-600 hover:via-amber-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                  >
                    📋 Enquire Now
                    <ArrowRightIcon className="ml-3 h-6 w-6" />
                  </button>
                ) : bookEnabled ? (
                  <Link
                    to={`/book-now?package=${displayData._id}&priceType=${selectedPriceType}`}
                    className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 text-white font-bold py-4 md:py-5 px-8 md:px-10 rounded-xl hover:from-orange-600 hover:via-amber-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                  >
                    🎯 Book Now
                    <ArrowRightIcon className="ml-3 h-6 w-6" />
                  </Link>
                ) : null}
              </div>
            ) : (
              <Link
                to="/packages"
                className="inline-flex items-center bg-white text-orange-600 font-semibold py-4 px-10 rounded-xl hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-300"
              >
                <span className="text-lg">Explore Packages</span>
                <ArrowRightIcon className="ml-3 h-5 w-5" />
              </Link>
            )}
          </div>
        </div>

        {/* Enquiry Form Modal */}
        {showEnquiryForm && displayData && (
          <EnquiryForm
            packageId={displayData._id}
            packageName={displayData.name}
            hasActiveLastMinuteDeal={displayData.hasActiveLastMinuteDeal}
            selectedPriceType={selectedPriceType}
            onClose={() => setShowEnquiryForm(false)}
            onSuccess={() => {
              // Optional: Show success message or redirect
              console.log('Enquiry submitted successfully!');
            }}
          />
        )}

        {/* Booking Modal */}
        <AnimatePresence>
          {showBookingModal && displayData && (
            <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => !paymentProcessing && setShowBookingModal(false)}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              />
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-auto overflow-hidden z-10"
              >
                {/* Processing Overlay */}
                <AnimatePresence>
                  {paymentProcessing && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex items-center justify-center"
                    >
                      <div className="text-center">
                        <div className="relative w-20 h-20 mx-auto mb-4">
                          <div className="absolute inset-0 border-4 border-orange-100 rounded-full"></div>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-0 border-4 border-orange-600 border-t-transparent rounded-full"
                          ></motion.div>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Processing Payment</h2>
                        <p className="text-gray-600 mt-1">Please don't refresh the page</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Header Image/Pattern */}
                <div className="h-32 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 relative">
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
                  <button
                    onClick={() => !paymentProcessing && setShowBookingModal(false)}
                    className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full text-white transition-all hover:scale-110"
                    disabled={paymentProcessing}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                  <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center border-4 border-white">
                    <ShieldCheckIcon className="w-10 h-10 text-orange-500" />
                  </div>
                </div>

                <div className="pt-12 pb-8 px-6 md:px-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                  {/* Header Text */}
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Finalize Booking</h2>
                    <p className="text-gray-500 text-sm mt-1">{displayData.name}</p>
                    <div className="mt-4 inline-flex flex-col items-center">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Total Amount</span>
                      <div className="px-6 py-2 bg-orange-50 text-orange-600 rounded-2xl font-black text-2xl border border-orange-100 shadow-sm">
                        {currency} {getPackagePrice().toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  <AnimatePresence>
                    {paymentError && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-6 overflow-hidden"
                      >
                        <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3">
                          <ExclamationTriangleIcon className="w-6 h-6 text-red-500 flex-shrink-0" />
                          <p className="text-red-700 text-sm leading-snug">{paymentError}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Loading Animation Before PayPal */}
                  <AnimatePresence>
                    {showLoadingBeforePayPal && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="py-12 text-center"
                      >
                        <div className="relative w-16 h-16 mx-auto mb-6">
                          <div className="absolute inset-0 border-4 border-orange-50 rounded-full"></div>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full"
                          ></motion.div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Almost there!</h3>
                        <p className="text-gray-500">Connecting to secure PayPal gateway...</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Customer Form & Payment Selection */}
                  {!showPayPalButtons && !showLoadingBeforePayPal && (
                    <div className="space-y-6">
                      {/* Section Title */}
                      <div className="flex items-center gap-2 text-gray-900 font-bold border-b border-gray-100 pb-2">
                        <UsersIcon className="w-5 h-5 text-orange-500" />
                        <h3>Guest Information</h3>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-500 uppercase ml-1">First Name</label>
                          <input
                            type="text"
                            value={bookingFormData.firstName}
                            onChange={(e) => setBookingFormData(prev => ({ ...prev, firstName: e.target.value }))}
                            className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                            placeholder="John"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-500 uppercase ml-1">Last Name</label>
                          <input
                            type="text"
                            value={bookingFormData.lastName}
                            onChange={(e) => setBookingFormData(prev => ({ ...prev, lastName: e.target.value }))}
                            className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                            placeholder="Doe"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Email Address</label>
                        <input
                          type="email"
                          value={bookingFormData.email}
                          onChange={(e) => setBookingFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                          placeholder="john@example.com"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Mobile Number</label>
                        <div className="flex gap-3">
                          <select
                            value={bookingFormData.countryCode}
                            onChange={(e) => setBookingFormData(prev => ({ ...prev, countryCode: e.target.value }))}
                            className="w-24 px-3 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 font-bold"
                          >
                            <option value="+61">🇦🇺 +61</option>
                            <option value="+1">🇺🇸 +1</option>
                            <option value="+44">🇬🇧 +44</option>
                            <option value="+91">🇮🇳 +91</option>
                          </select>
                          <input
                            type="tel"
                            value={bookingFormData.phone}
                            onChange={(e) => setBookingFormData(prev => ({ ...prev, phone: e.target.value }))}
                            className="flex-1 px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                            placeholder="412 345 678"
                          />
                        </div>
                      </div>

                      {/* Payment Method Selection - Visual Cards */}
                      {paymentSettings?.paypal?.enabled && paymentSettings?.flywire?.enabled && (
                        <div className="space-y-3 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 text-gray-900 font-bold">
                            <CreditCardIcon className="w-5 h-5 text-orange-500" />
                            <h3>Choose Payment Method</h3>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <button
                              onClick={() => setPaymentMethod('paypal')}
                              className={`p-4 rounded-3xl border-2 transition-all flex flex-col items-center gap-2 hover:shadow-md ${paymentMethod === 'paypal'
                                  ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                                  : 'border-gray-100 bg-white hover:border-orange-200'
                                }`}
                            >
                              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${paymentMethod === 'paypal' ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                                <span className="text-2xl">💳</span>
                              </div>
                              <span className="font-bold text-sm">PayPal</span>
                              <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-tighter">Debit/Credit Card</span>
                            </button>

                            <button
                              onClick={() => setPaymentMethod('flywire')}
                              className={`p-4 rounded-3xl border-2 transition-all flex flex-col items-center gap-2 hover:shadow-md ${paymentMethod === 'flywire'
                                  ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                                  : 'border-gray-100 bg-white hover:border-orange-200'
                                }`}
                            >
                              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${paymentMethod === 'flywire' ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                                <span className="text-2xl">🌐</span>
                              </div>
                              <span className="font-bold text-sm">Flywire</span>
                              <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-tighter">Bank Transfer</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Proceed Button */}
                      <button
                        onClick={handleProceedToPayment}
                        disabled={paymentProcessing}
                        className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black py-4 px-8 rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-xl shadow-orange-200 disabled:opacity-50 active:scale-[0.98] mt-4"
                      >
                        {paymentProcessing ? 'INITIALIZING...' : 'BOOK & PAY NOW'}
                      </button>
                    </div>
                  )}

                  {/* PayPal Buttons Section */}
                  {showPayPalButtons && paymentSettings?.paypal?.clientId && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-6"
                    >
                      <button
                        onClick={() => setShowPayPalButtons(false)}
                        className="text-orange-600 hover:text-orange-700 text-sm font-bold flex items-center gap-1.5 transition-colors group"
                      >
                        <span className="group-hover:-translate-x-1 transition-transform">←</span>
                        Change payment method
                      </button>
                      
                      <div className="bg-gray-50 p-6 rounded-3xl">
                        <PayPalScriptProvider options={{
                          clientId: paymentSettings.paypal.clientId,
                          currency: currency,
                          intent: 'capture'
                        }}>
                          <PayPalButtons
                            style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                            createOrder={createPayPalOrder}
                            onApprove={onPayPalApprove}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              setPaymentError('PayPal payment failed. Please try again.');
                            }}
                            onCancel={() => {
                              setShowPayPalButtons(false);
                              setPaymentError('Payment cancelled.');
                            }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Footer Security Badge */}
                <div className="bg-gray-50/50 py-4 px-8 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    <ShieldCheckIcon className="w-4 h-4 text-green-500" />
                    <span>Bank-Level Security</span>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-8 h-5 bg-white border border-gray-100 rounded-sm opacity-50"></div>
                    <div className="w-8 h-5 bg-white border border-gray-100 rounded-sm opacity-50"></div>
                    <div className="w-8 h-5 bg-white border border-gray-100 rounded-sm opacity-50"></div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PackageDetailPage;
